import { styled } from '@mui/material/styles';
import { ChangeEvent, useState, useEffect } from 'react';
import { QTabContext, QTabList, QTab, QTabPanel, QContainer, QThemeProvider } from '@kw/quokka-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useBeforeUnload } from 'react-router-dom';
import { Integrations } from './Integrations';
import { OrganizationSettings } from './OrganizationSettings';
import { UserManagement } from './UserManagement';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { BackgroundProgressBar } from '../BackgroundProgressBar/BackgroundProgressBar';
import { useReloadRestore } from '../Applications/GuardianReport/utils/useReloadRestore';
import { useOrg } from '../Organization/org-provider';
import { orgHasLicense } from '../Organization/org-utils';

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

export function Settings() {
  const { settingsTabFocus, setSettingsTabFocus, shouldRestore, setShouldRestore } = useReloadRestore();
  const [activeTab, setActiveTab] = useState<string>(settingsTabFocus.root);

  const handleChange = (_event: ChangeEvent<HTMLButtonElement>, tab: string) => {
    setActiveTab(tab);
  };
  const flags: LaunchDarklyFlags['flags'] = useFlags();

  useBeforeUnload(() => {
    setShouldRestore(true);
  });

  useEffect(() => {
    setSettingsTabFocus({ root: activeTab });
    setShouldRestore(false);
  }, [activeTab]);

  useEffect(() => {
    if (!shouldRestore) {
      setSettingsTabFocus({ root: '0' });
    }
  }, []);

  const { orgData } = useOrg();
  const isMdmLicense =  orgHasLicense(orgData, 'mdm-integration');

  return (
    <QThemeProvider>
      <QContainer maxWidth={false}>
        <BackgroundProgressBar />
        {isMdmLicense ? (
          <QTabContext value={activeTab}>
            <QTabList tabs="true" onChange={handleChange}>
              <StyledQTab label="Integrations" value="0" {...a11yProps(0)} />
              <StyledQTab label="Organization Settings" value="1" {...a11yProps(1)} />
              <StyledQTab label="Users" value="2" {...a11yProps(2)} />
            </QTabList>
            <StyledQTabPanel tabid="0" value="0" index={0}>
              <Integrations />
            </StyledQTabPanel>
            <StyledQTabPanel tabid="1" value="1" index={1}>
              <OrganizationSettings />
            </StyledQTabPanel>
            <StyledQTabPanel tabid="2" value="2" index={2}>
              <UserManagement />
            </StyledQTabPanel>
          </QTabContext>
        ) : ( // we have two exclusive licenses here: MDM, and PDM
          <QTabContext value={activeTab}>
            <QTabList tabs="true" onChange={handleChange}>
              <StyledQTab label="Organization Settings" value="0" {...a11yProps(0)} />
              <StyledQTab label="Users" value="1" {...a11yProps(1)} />
            </QTabList>
            <StyledQTabPanel tabid="0" value="0" index={0}>
              <OrganizationSettings />
            </StyledQTabPanel>
            <StyledQTabPanel tabid="1" value="1" index={1}>
              <UserManagement />
            </StyledQTabPanel>
          </QTabContext>
        )}
      </QContainer>
    </QThemeProvider>
  );
}

const StyledQTab = styled(QTab)`
  text-transform: capitalize;
  font-size: 16px;
  font-weight: 400;
  color: #757575;
  &.Mui-selected {
    color: #000;
    font-weight: 700;
  }
  padding: 20px 40px;
`;

const StyledQTabPanel = styled(QTabPanel)`
  padding: 24px 0;
`;
